/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of selected options
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

(function () {
  // Config
  const DEFAULT_MAX_QTY = 12;
  const WARNING_MS = 4000;

  let warningHideTimer = null;
  let atcRestoreTimer = null;
  let styleInjected = false;

  // Helpers
  function toInt(val, fallback) {
    const n = parseInt(val, 10);
    return Number.isFinite(n) ? n : fallback;
  }

  function getMaxQtyFromWarning(warningEl) {
    if (!warningEl) return DEFAULT_MAX_QTY;
    return toInt(warningEl.getAttribute('data-max'), DEFAULT_MAX_QTY);
  }

  // Find warning marker on product page
  function getProductFormWarning() {
    return (
      document.querySelector('product-form .max-qty-warning[data-variant-id]') ||
      document.querySelector('form[action*="/cart/add"] .max-qty-warning[data-variant-id]') ||
      null
    );
  }

  // Hide theme max qty message so only one warning is shown
  function hideThemeQtyError(scope) {
    const root = scope || document;

    const selectors = [
      '.product-form__error-message-wrapper',
      '.product-form__error-message',
      '[data-product-form-error-message]',
      '.form__message',
      '.shopify-error-message',
      '[role="alert"]'
    ];

    selectors.forEach(function (sel) {
      root.querySelectorAll(sel).forEach(function (el) {
        const txt = (el.textContent || '').toLowerCase();
        if (txt.indexOf('max quantity reached') !== -1) {
          el.hidden = true;
          el.style.display = 'none';
        }
      });
    });
  }

  // Suppress common success tick icons while showing the X
  function ensureAtcErrorStyle() {
    if (styleInjected) return;
    styleInjected = true;

    const style = document.createElement('style');
    style.type = 'text/css';
    style.textContent =
      '.max-qty-atc-error svg,' +
      '.max-qty-atc-error .icon,' +
      '.max-qty-atc-error [class*="icon"],' +
      '.max-qty-atc-error [aria-live],' +
      '.max-qty-atc-error [role="status"]{display:none !important;}' +
      '.max-qty-atc-x{display:inline-block !important;}';
    document.head.appendChild(style);
  }

  // Find nearest warning to the control being used
  function findNearestWarning(fromEl) {
    if (!fromEl) return document.querySelector('.max-qty-warning');

    const container =
      fromEl.closest('tr.cart-item') ||
      fromEl.closest('cart-item') ||
      fromEl.closest('[data-variant-id]') ||
      fromEl.closest('product-form') ||
      fromEl.closest('form') ||
      fromEl.closest('.product') ||
      document;

    return container.querySelector('.max-qty-warning') || document.querySelector('.max-qty-warning');
  }

  // Show warning and auto hide
  function showWarningTemporarily(fromEl) {
    const warning = findNearestWarning(fromEl);
    if (!warning) return;

    const scope =
      warning.closest('tr.cart-item') ||
      warning.closest('cart-item') ||
      warning.closest('product-form') ||
      warning.closest('form') ||
      document;

    hideThemeQtyError(scope);

    warning.hidden = false;

    if (warningHideTimer) window.clearTimeout(warningHideTimer);
    warningHideTimer = window.setTimeout(function () {
      warning.hidden = true;
      hideThemeQtyError(scope);
    }, WARNING_MS);
  }

  // Show red X when add is blocked due to cart already at max
  function showAddToCartErrorX() {
    const warning = getProductFormWarning();
    if (!warning) return;

    const scope =
      warning.closest('product-form') ||
      warning.closest('form') ||
      warning.closest('.product') ||
      document;

    const btn =
      scope.querySelector('button[name="add"]') ||
      scope.querySelector('button[type="submit"][name="add"]') ||
      scope.querySelector('button[type="submit"]');

    if (!btn) return;

    ensureAtcErrorStyle();

    if (!btn.dataset.maxQtyOriginalHtml) {
      btn.dataset.maxQtyOriginalHtml = btn.innerHTML;
    }

    if (atcRestoreTimer) window.clearTimeout(atcRestoreTimer);

    btn.classList.add('max-qty-atc-error');
    btn.classList.remove('is-added', 'added', 'success', 'loading');

    btn.innerHTML =
      '<span class="max-qty-atc-x" aria-hidden="true" style="color:#b00020;font-weight:700;font-size:18px;line-height:1;">✕</span>';

    atcRestoreTimer = window.setTimeout(function () {
      btn.classList.remove('max-qty-atc-error');
      if (btn.dataset.maxQtyOriginalHtml) {
        btn.innerHTML = btn.dataset.maxQtyOriginalHtml;
      }
    }, WARNING_MS);
  }

  // Quantity input discovery
  function findQtyInput(scope) {
    const root = scope || document;

    return (
      root.querySelector('input[name="quantity"]') ||
      root.querySelector('input.quantity__input') ||
      root.querySelector('input[data-quantity-input]') ||
      root.querySelector('quantity-input input') ||
      root.querySelector('input[type="number"]')
    );
  }

  // Warn on attempts even if UI clamps at max
  function bindAttemptWarning(qtyInput, plusBtn, maxQty) {
    if (!qtyInput) return;

    const boundKey = 'maxQtyAttemptBound_' + String(maxQty);
    if (qtyInput.dataset[boundKey]) return;
    qtyInput.dataset[boundKey] = '1';

    function currentVal() {
      const v = parseInt(qtyInput.value, 10);
      return Number.isFinite(v) ? v : null;
    }

    if (plusBtn) {
      const plusKey = 'maxQtyAttemptPlusBound_' + String(maxQty);
      if (!plusBtn.dataset[plusKey]) {
        plusBtn.dataset[plusKey] = '1';
        plusBtn.addEventListener('click', function () {
          const v = currentVal();
          if (v !== null && v >= maxQty) showWarningTemporarily(qtyInput);
        });
      }
    }

    qtyInput.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowUp') return;
      const v = currentVal();
      if (v !== null && v >= maxQty) showWarningTemporarily(qtyInput);
    });

    qtyInput.addEventListener('beforeinput', function (e) {
      if (!e || typeof e.inputType !== 'string') return;

      const type = e.inputType;
      const isText =
        type === 'insertText' ||
        type === 'insertFromPaste' ||
        type === 'insertReplacementText';

      if (!isText) return;

      const data = typeof e.data === 'string' ? e.data : '';
      const start =
        typeof qtyInput.selectionStart === 'number'
          ? qtyInput.selectionStart
          : qtyInput.value.length;
      const end =
        typeof qtyInput.selectionEnd === 'number'
          ? qtyInput.selectionEnd
          : qtyInput.value.length;

      const nextStr = qtyInput.value.slice(0, start) + data + qtyInput.value.slice(end);
      const nextVal = parseInt(nextStr, 10);

      if (Number.isFinite(nextVal) && nextVal > maxQty) {
        showWarningTemporarily(qtyInput);
      }
    });
  }

  // Product page clamp (only when warning marker exists)
  function bindLimit() {
    const warning = getProductFormWarning();
    if (!warning) return;

    const maxQty = getMaxQtyFromWarning(warning);

    const scope =
      warning.closest('product-form') ||
      warning.closest('form') ||
      warning.closest('.product') ||
      document;

    const qtyInput = findQtyInput(scope);
    if (!qtyInput) return;

    qtyInput.setAttribute('max', String(maxQty));

    const row = qtyInput.closest('.product-info__add-to-cart') || scope;

    const plusBtn =
      row.querySelector('[data-quantity-plus]') ||
      row.querySelector('.quantity__button[name="plus"]') ||
      row.querySelector('button[name="plus"]') ||
      row.querySelector('button[aria-label*="+"]');

    bindAttemptWarning(qtyInput, plusBtn, maxQty);

    function clampIfNeeded() {
      const val = parseInt(qtyInput.value, 10);
      if (!Number.isFinite(val)) return;

      if (val > maxQty) {
        qtyInput.value = String(maxQty);
        showWarningTemporarily(qtyInput);
        qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    qtyInput.addEventListener('input', clampIfNeeded);
    qtyInput.addEventListener('change', clampIfNeeded);
    qtyInput.addEventListener('keyup', clampIfNeeded);
    qtyInput.addEventListener('blur', clampIfNeeded);

    if (plusBtn && !plusBtn.dataset.maxQtyPlusBound) {
      plusBtn.dataset.maxQtyPlusBound = '1';
      plusBtn.addEventListener('click', function () {
        window.setTimeout(function () {
          clampIfNeeded();
        }, 0);
      });
    }

    if (!row.dataset.maxQtyRowBound) {
      row.dataset.maxQtyRowBound = '1';
      row.addEventListener('click', function () {
        window.setTimeout(function () {
          clampIfNeeded();
        }, 0);
      });
    }

    clampIfNeeded();
  }

  // Cart and drawer clamp (rows must have data-max-qty and data-variant-id)
  function bindCartLimit() {
    const rows = document.querySelectorAll('tr.cart-item[data-max-qty][data-variant-id]');
    if (!rows.length) return;

    rows.forEach(function (row) {
      const rowMaxQty = toInt(row.getAttribute('data-max-qty'), DEFAULT_MAX_QTY);

      const boundKey = 'maxQtyCartBound_' + String(rowMaxQty);
      if (row.dataset[boundKey]) return;
      row.dataset[boundKey] = '1';

      const qtyInput = row.querySelector('input[name="updates[]"]');
      if (!qtyInput) return;

      qtyInput.setAttribute('max', String(rowMaxQty));

      const plusBtn =
        row.querySelector('button[name="plus"]') ||
        row.querySelector('[data-quantity-plus]') ||
        row.querySelector('.quantity__button[name="plus"]');

      bindAttemptWarning(qtyInput, plusBtn, rowMaxQty);

      function clampCart() {
        const val = parseInt(qtyInput.value, 10);
        if (!Number.isFinite(val)) return;

        if (val > rowMaxQty) {
          qtyInput.value = String(rowMaxQty);
          showWarningTemporarily(qtyInput);
          qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
          qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      qtyInput.addEventListener('input', clampCart);
      qtyInput.addEventListener('change', clampCart);
      qtyInput.addEventListener('keyup', clampCart);
      qtyInput.addEventListener('blur', clampCart);

      if (plusBtn && !plusBtn.dataset.maxQtyPlusCartBound) {
        plusBtn.dataset.maxQtyPlusCartBound = '1';
        plusBtn.addEventListener('click', function () {
          window.setTimeout(function () {
            clampCart();
          }, 0);
        });
      }

      clampCart();
    });
  }

  // Cart helpers
  function getCartJson() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      return r.json();
    });
  }

  function cartQtyForVariant(cart, variantId) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    for (let i = 0; i < cart.items.length; i++) {
      const it = cart.items[i];
      if (it && it.id === variantId) return it.quantity || 0;
    }
    return 0;
  }

  // Prevent adding beyond max total in cart for the limited variant (only when warning marker exists)
  function bindAjaxAddCap() {
    const warning = getProductFormWarning();
    if (!warning) return;

    const maxQty = getMaxQtyFromWarning(warning);

    const variantIdAttr = warning.getAttribute('data-variant-id');
    const limitedVariantId = parseInt(variantIdAttr, 10);
    if (!Number.isFinite(limitedVariantId)) return;

    if (window.__maxQtyFetchWrapped) return;
    window.__maxQtyFetchWrapped = true;

    const originalFetch = window.fetch;

    window.fetch = function (input, init) {
      const url =
        typeof input === 'string'
          ? input
          : input && input.url
          ? input.url
          : '';

      const isAdd = url.indexOf('/cart/add') !== -1;
      if (!isAdd) return originalFetch.apply(this, arguments);

      init = init || {};
      const body = init.body;

      function parseAddBody() {
        if (!body) return { id: null, qty: 1 };

        if (body instanceof FormData) {
          const id = parseInt(body.get('id'), 10);
          const q = parseInt(body.get('quantity') || '1', 10);
          return { id: Number.isFinite(id) ? id : null, qty: Number.isFinite(q) ? q : 1 };
        }

        if (typeof body === 'string') {
          const params = new URLSearchParams(body);
          const id = parseInt(params.get('id'), 10);
          const q = parseInt(params.get('quantity') || '1', 10);
          return { id: Number.isFinite(id) ? id : null, qty: Number.isFinite(q) ? q : 1 };
        }

        if (body && typeof body === 'object') {
          const id = parseInt(body.id, 10);
          const q = parseInt(body.quantity || 1, 10);
          return { id: Number.isFinite(id) ? id : null, qty: Number.isFinite(q) ? q : 1 };
        }

        return { id: null, qty: 1 };
      }

      const parsed = parseAddBody();

      if (parsed.id !== limitedVariantId) {
        return originalFetch.apply(this, arguments);
      }

      return getCartJson().then(function (cart) {
        const existing = cartQtyForVariant(cart, limitedVariantId);
        const remaining = maxQty - existing;

        if (remaining <= 0) {
          showWarningTemporarily(warning);
          showAddToCartErrorX();
          hideThemeQtyError(document);

          return Promise.resolve(
            new Response(JSON.stringify({ ok: false, message: 'Max quantity reached' }), {
              status: 200,
              headers: { 'Content-Type': 'application/json' }
            })
          );
        }

        if (parsed.qty > remaining) {
          showWarningTemporarily(warning);
          hideThemeQtyError(document);
          parsed.qty = remaining;
        }

        if (body instanceof FormData) {
          body.set('quantity', String(parsed.qty));
          init.body = body;
        } else if (typeof body === 'string') {
          const params = new URLSearchParams(body);
          params.set('quantity', String(parsed.qty));
          init.body = params.toString();
        } else if (body && typeof body === 'object') {
          body.quantity = parsed.qty;
          init.body = body;
        }

        return originalFetch.call(this, input, init);
      });
    };
  }

  // Boot
  function boot() {
    bindLimit();
    bindCartLimit();
    bindAjaxAddCap();
    hideThemeQtyError(document);
  }

  document.addEventListener('DOMContentLoaded', boot);

  const mo = new MutationObserver(function () {
    boot();
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();